import React, { useEffect, useRef } from 'react'
import './jobs-list.scss'
import classnames from 'classnames'
import clone from 'clone-deep'
import * as deepEqual from 'react-fast-compare'
import DataTableView from '../../components/data-table-view/data-table-view'
import { useMountedRef } from '../../hooks/use-mounted-ref'
import { fmtDateString, fmtNumberAsDelineatedCount } from '../../helpers/formatting'
import { useAxiosCancelToken } from '../../hooks/use-axios-cancel-token'
import EntityDisplay, { EntityTitle } from '../../components/data-table-view/entity-display'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { fetchJobFilters, fetchJobs } from '@halo/api/brine.api'
import { CloseCircleOutlined, CopyOutlined, Loading3QuartersOutlined, ReloadOutlined } from '@ant-design/icons'
import { Badge, Button, Dropdown, Menu, notification, Table, Tooltip } from 'antd'
import moment from 'moment-timezone'
import { useLocation } from 'react-router'
import queryString from 'query-string'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { collateConfig, buildApiOrderConfig } from '@halo/components/data-table-view/helpers'
import { useRefState } from '@halo/hooks/use-ref-state'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { useRefEffect } from '@halo/hooks/use-ref-effect'
import ExpandedJobView from '@halo/components/jobs-list/expanded-job-view'
import CopyToClipboard from 'react-copy-to-clipboard'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { DataSourceResolver } from '@halo/components/data-table-view/types'
import { handleJobMarkAsFailed, handleJobClone } from '@halo/components/jobs-list/helpers'
import { observer } from 'mobx-react'
import { JOB_ACTION } from '@halo/components/jobs-list/enums'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const JobDateTime = observer(({ date }: { date: string }) => {
    const appState = useService<AppState>(AppState)

    return !date ? (
        <>--</>
    ) : (
        <EntityDisplay
            top={{ left: <span className="job-date">{fmtDateString(date, 'll')}</span> }}
            mid={{
                left: (
                    <span className="job-time">{fmtDateString(date, 'LT z', appState.displayTimeZone ?? 'utc')}</span>
                ),
            }}
        />
    )
})

const handleCopied = () => {
    notification.destroy()
    notification.success({
        message: '',
        description: 'Copied',
    })
}

const renderTotal = (total: number, config: any): React.ReactNode => {
    const {
        pagination: { current, pageSize },
    } = clone(config)
    const start = fmtNumberAsDelineatedCount((current - 1) * pageSize + 1)
    const end = fmtNumberAsDelineatedCount(current * pageSize)
    const ttl = fmtNumberAsDelineatedCount(total)

    return total <= pageSize
        ? `Showing ${ttl} of ${ttl} Total Job(s)`
        : `Showing ${start}-${end} of ${ttl} Total Job(s)`
}

const JobsList = () => {
    const appState = useService<AppState>(AppState)

    const [_mounted, runIfMounted] = useMountedRef()

    /**
     * Handle QueryString variables for
     *
     * @param job_id - defaults search input value
     * @param expand_job_id - expand auto selected id if provided
     */
    const { search: rawQs } = useLocation()
    const qs = queryString.parse(rawQs.replace(/^\?/, ''))
    const qsJobId = (qs.job_id ?? qs.jobId ?? '').toString()
    const qsExpandJobId = (qs.expand_job_id ?? qs.expandJobId ?? false).toString().toLowerCase() === 'true'
    const defaultExpandedRowKeys: string[] = []
    if (qsJobId && qsExpandJobId) {
        defaultExpandedRowKeys.push(qsJobId)
    }

    /**
     * Data View configuration & state manager
     *
     * @param loading
     * @param data - current page data
     * @param pagination - AntD pagination config
     * @param filters - AntD filters config + { tableSearchValue: string }
     * @param sortOrder - AntD sortOrder config
     */
    // @ts-ignore
    const [jobs, setJobs] = useRefState<any>({
        loading: false,
        data: undefined,
        pagination: {
            pageSize: 100,
            current: 1,
            total: 100,

            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showQuickJumper: true,
            // @ts-ignore
            showTotal: (total: number) => renderTotal(total, jobs.current),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }
                if (type === 'page' || /^jump/.test(type)) {
                    return ''
                }

                return originalElement
            },
        } as TablePaginationConfig,
        filters: stripUndefined({
            tableSearchValue: qsJobId,
        }),
        sortOrder: {
            columnKey: 'created_at',
            field: 'created_at',
            order: 'descend',
        },
    })

    const [paginationLinks, setPaginationLinks] = React.useState<null | IApiResponsePaginationLinks>(null)
    const [apiFilters, refreshApiFilters] = useRefEffect(() => {
        return fetchJobFilters({ showLoadingScreen: false })
    }, [false])

    const [cancelToken, resetCancelToken] = useAxiosCancelToken()
    const [isTableExpanded, setIsTableExpanded] = useRefState(false)
    const previousJobIds = useRef<string[] | undefined>(undefined)
    const refreshRef = useRef({
        timeout: undefined,
        timestamp: moment(),
    })

    /**
     * DataSourceResolver
     * Pushly extension for AntD table change event
     * accepts undefined values allowing inheritance
     *
     * @param [pagination] - AntD pagination config
     * @param [filters] - AntD filters config + { tableSearchValue: string }
     * @param [sortOrder] - AntD sortOrder config
     * @param [extra] - AntD sortOrder config
     */
    const fetchData: DataSourceResolver<any> = async (pagination, filters, sortOrder, extra): Promise<void> => {
        // clear previous auto-refresh handler
        clearTimeout(refreshRef.current.timeout)

        setJobs({ ...jobs.current, loading: true })
        let data: any[] = []

        // set previously loaded ids
        if (!!extra?.currentDataSource) {
            previousJobIds.current = extra.currentDataSource.map((job: any) => job.id)
        }

        let paginationConfig = clone(collateConfig(pagination, jobs.current.pagination))
        let filtersConfig = clone(collateConfig(filters, jobs.current.filters))
        let sortConfig = clone(collateConfig(sortOrder, jobs.current.sortOrder))

        const pageSizeChanged = paginationConfig.pageSize !== jobs.current.pagination?.pageSize
        const filtersChanged = !deepEqual(filtersConfig, jobs.current.filters)
        const sortChanged = !deepEqual(sortConfig, jobs.current.sortOrder)
        const changesDetected = pageSizeChanged || filtersChanged || sortChanged

        // reset current page on filter/sort/size change
        if (changesDetected) {
            paginationConfig.current = 1
        }

        const searchVal = filtersConfig.tableSearchValue?.trim()
        const orderConfig = buildApiOrderConfig(sortConfig)

        const query = stripUndefined({
            limit: paginationConfig?.pageSize,
            page: paginationConfig?.current,
            search: !searchVal ? undefined : searchVal,
            status: !filtersConfig.status?.length ? undefined : filtersConfig.status.join(','),
            target_name: !filtersConfig.target_name?.length ? undefined : filtersConfig.target_name.join(','),
            order: !orderConfig ? undefined : JSON.stringify(orderConfig),
        })

        resetCancelToken()
        const res = await fetchJobs({
            query,
            showLoadingScreen: false,
            cancelToken: cancelToken.current,
        })

        if (res && res.ok) {
            data = res.data!
            setPaginationLinks(res.meta.links ?? paginationLinks)
        }

        // initial set / reset
        if (
            paginationConfig.current === 1 &&
            (!previousJobIds.current || pageSizeChanged || filtersChanged || sortChanged)
        ) {
            previousJobIds.current = data.map((job) => job.id)
        }

        runIfMounted(() => {
            // set timestamp ref before setJobs
            // to ensure proper value on rerender
            refreshRef.current.timestamp = moment()

            setJobs({
                loading: false,
                data,
                pagination: paginationConfig,
                filters: filtersConfig,
                sortOrder: sortConfig,
            })

            // start timer for next refresh
            processAutoRefresh()
        })
    }

    /**
     * canAutoRefresh determines ability
     * to fetch data after refresh timer
     *
     * disabled auto-refresh if search is
     * populated or any rows are expanded
     */
    const canAutoRefresh = () => {
        const hasSearchValue = !!jobs.current.filters?.tableSearchValue
        return !isTableExpanded.current && !hasSearchValue
    }

    const processAutoRefresh = () => {
        clearTimeout(refreshRef.current.timeout)
        refreshRef.current.timeout = setTimeout(
            () => {
                if (canAutoRefresh()) {
                    fetchData()
                }
            },
            3 * 60 * 1_000,
        )
    }

    const handleRowAction = (action: string | number, job: any) => {
        if (action === JOB_ACTION.FORCE_FAIL) {
            handleJobMarkAsFailed(job, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_ACTION.CLONE) {
            handleJobClone(job, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        }
    }

    /**
     * Initial data load runs once on first mount/render
     */
    useEffect(() => {
        fetchData()
    }, [false])

    return (
        <div className={classnames('jobs-manager-list')}>
            <DataTableContext.Provider
                value={{
                    loading: jobs.current.loading,
                    currentDataSource: jobs.current.data,
                    resolveDataSource: fetchData,
                }}
            >
                <DataTableView
                    searchPlaceholder="Search ID or name"
                    header={
                        <>
                            <span>Jobs</span>
                            <span className="last-updated">
                                Last Updated @ {refreshRef.current.timestamp.format('LT')}
                                {!canAutoRefresh() && <span> (PAUSED) </span>}
                                <ReloadOutlined spin={jobs.current.loading} onClick={() => fetchData()} />
                            </span>
                        </>
                    }
                    filtersDefaultValue={clone(jobs.current.filters)}
                    filters={apiFilters.current?.then(({ ok, data }) => {
                        const filters: any[] = []
                        if (ok && Array.isArray(data.targets)) {
                            filters.push({
                                className: 'job-filter target-name-filter',
                                dataIndex: 'target_name',
                                multiple: true,
                                maxTagCount: 1,
                                placeholder: 'All targets',
                                optionPrefix: 'Target',
                                options: data.targets.map((tgt: any) => ({
                                    group: tgt.type,
                                    value: tgt.name,
                                    label: tgt.name === 'submarine-communications-cable' ? 'scc' : tgt.name,
                                })),
                            })
                        }

                        filters.push({
                            className: 'job-filter status-filter',
                            dataIndex: 'status',
                            multiple: true,
                            maxTagCount: 1,
                            placeholder: 'All statuses',
                            optionPrefix: 'Status',
                            options: [
                                { label: 'Submitted', value: 'submitted' },
                                { label: 'Queued', value: 'queued' },
                                { label: 'Running', value: 'running' },
                                { label: 'Completed', value: 'completed' },
                                { label: 'Failed', value: 'failed' },
                            ],
                        })

                        return filters
                    })}
                >
                    <Table
                        size="small"
                        bordered={false}
                        // scroll.y used to freeze header row
                        // scroll.x used to fix bug with col width and scroll.y
                        // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                        scroll={{ y: 549, x: 'max-content' }}
                        rowKey="id"
                        rowClassName={(dto: any) => {
                            const classNames = [`status-${dto.status}`]

                            if (Array.isArray(previousJobIds.current) && !previousJobIds.current.includes(dto.id)) {
                                classNames.push('new-row')
                            }

                            return classNames.join(' ')
                        }}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    setJobs({
                                        ...jobs.current,
                                        pagination: {
                                            ...jobs.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                                onNext={(_ev, _link, config) => {
                                    setJobs({
                                        ...jobs.current,
                                        pagination: {
                                            ...jobs.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                            />
                        )}
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No Jobs" icon="deployment-unit" />,
                        }}
                        loading={
                            !jobs.current.loading
                                ? false
                                : {
                                      indicator: <Loading3QuartersOutlined spin={true} />,
                                  }
                        }
                        dataSource={jobs.current.data}
                        onChange={fetchData}
                        onExpandedRowsChange={(rowKeys) => {
                            const expanded = rowKeys.length > 0
                            setIsTableExpanded(expanded)
                            if (!expanded) {
                                processAutoRefresh()
                            }
                        }}
                        expandedRowRender={(job: any, index: number, indent: number, expanded: boolean) => (
                            <ExpandedJobView job={job} index={index} indent={indent} expanded={expanded} />
                        )}
                        defaultExpandedRowKeys={defaultExpandedRowKeys}
                    >
                        <Table.Column
                            title="Name"
                            className="name"
                            dataIndex="name"
                            render={(name, job: any) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: <EntityTitle>{name}</EntityTitle>,
                                        }}
                                        mid={{
                                            left: (
                                                <span className="job-id">
                                                    <span className="copy">
                                                        <Tooltip title="Copy">
                                                            <CopyToClipboard
                                                                onCopy={handleCopied}
                                                                text={JSON.stringify(job.id)}
                                                            >
                                                                <CopyOutlined />
                                                            </CopyToClipboard>
                                                        </Tooltip>
                                                    </span>
                                                    {job.id}
                                                </span>
                                            ),
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="target"
                            title="Target"
                            dataIndex="target_config"
                            render={(config) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: (
                                                <EntityTitle>
                                                    {config.name === 'submarine-communications-cable'
                                                        ? 'scc'
                                                        : config.name}
                                                </EntityTitle>
                                            ),
                                        }}
                                        mid={{
                                            left: <span className="target-type">{config.type}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="status"
                            title="Status"
                            dataIndex="status"
                            align="center"
                            render={(status) => <Badge dot={true}>{status}</Badge>}
                        />
                        <Table.Column
                            className="started_at"
                            title="Started"
                            dataIndex="started_at"
                            align="center"
                            render={(dt) => <JobDateTime date={dt} />}
                        />
                        <Table.Column
                            className="ended_at"
                            title="Ended"
                            dataIndex="ended_at"
                            align="center"
                            render={(dt) => <JobDateTime date={dt} />}
                        />
                        <Table.Column
                            className="created_at"
                            title="Created"
                            dataIndex="created_at"
                            align="center"
                            render={(dt) => <JobDateTime date={dt} />}
                        />
                        {appState.currentUser?.isInternalDevUser && (
                            <Table.Column
                                className="actions"
                                title="Actions"
                                dataIndex="actions"
                                align="center"
                                render={(_, job: any) => {
                                    const status = job.status.toLowerCase()
                                    const canMarkAsFailed = status !== 'failed'

                                    return !canMarkAsFailed ? (
                                        <Button
                                            size="small"
                                            className="job-row-actions-btn"
                                            onClick={() => handleRowAction('job-clone', job)}
                                        >
                                            <CopyOutlined />
                                            <span>Clone</span>
                                        </Button>
                                    ) : (
                                        <Dropdown.Button
                                            size="small"
                                            className="job-row-actions-btn"
                                            trigger={['click']}
                                            onClick={() => handleRowAction(JOB_ACTION.CLONE, job)}
                                            overlay={
                                                <Menu
                                                    className="job-row-actions-dropdown-menu"
                                                    onClick={({ key }) => handleRowAction(key, job)}
                                                >
                                                    <Menu.Item key={JOB_ACTION.FORCE_FAIL}>
                                                        <CloseCircleOutlined />
                                                        <span>Mark as Failed</span>
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                        >
                                            <CopyOutlined />
                                            <span>Clone</span>
                                        </Dropdown.Button>
                                    )
                                }}
                            />
                        )}
                    </Table>
                </DataTableView>
            </DataTableContext.Provider>
        </div>
    )
}

export default JobsList
